/**
 * Renders a `quote_preview` view action (design D18): a read-only,
 * quote-document-like replica of the quote the assistant would create
 * (header, numbered sections with subtotals, item rows, totals, disclaimer).
 * Prices are computed by the backend; every word shown here is translated in
 * the user's language from the codes the payload carries. Nothing is
 * persisted; the real quote is created after the user confirmed.
 */
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useQuantityUnits } from "../../doc-items/useQuantityUnits.js";
import {
  ProductDetails,
  ProductThumb,
  useProductDisplayData,
} from "./productParts.js";
import {
  formatQuoteAmount,
  QUOTE_PREVIEW_CARD_ITEMS_PER_SECTION,
  type QuotePreviewItem,
  type QuotePreviewNote,
  type QuotePreviewSection,
  type QuotePreviewViewSpec,
} from "./quotePreviewSpec.js";

type Translate = ReturnType<typeof useTranslate>["t"];

function noteText(t: Translate, note: QuotePreviewNote): string {
  switch (note.code) {
    case "free":
      return t("No charge");
    case "noPrice":
      return t("No price");
    case "noLabourRate":
      return t("No labour rate known, so not priced");
    case "labourNotIncluded":
      return t("Working time not included: no labour rate known");
    case "productNotFound":
      return t("Product not found in the catalogue");
    case "noListPrice":
      return t("No list price in the catalogue");
    case "otherCurrency":
      return t("List price in {currency}", {
        currency: note.currency,
        ns: "Assistant",
      });
    case "productChosenLater":
      return note.preselected === null
        ? t("Product is chosen later")
        : t("Product is chosen later, preselected: {product}", {
            product: note.preselected,
            ns: "Assistant",
          });
    case "buildingBlock":
      return t("Building block {name}, shown without a price", {
        name: note.name,
        ns: "Assistant",
      });
    case "typeRecommendation":
      return t("Recommendation, picked later from the answers");
  }
}

/** A product's image: an expired signed URL is refetched once (see `useProductDisplayData`). */
function ProductItemThumb({
  item,
  articleNumber,
  supplierId,
}: {
  item: QuotePreviewItem;
  articleNumber: string;
  supplierId: string;
}) {
  const display = useProductDisplayData(
    {
      articleNumber,
      supplierId,
      title: item.title,
      imageUrl: item.imageUrl,
    },
    false
  );
  return (
    <ProductThumb imageUrl={display.imageUrl} onError={display.onImageError} />
  );
}

function PlainItemThumb({ imageUrl }: { imageUrl: string }) {
  const [failed, setFailed] = React.useState(false);
  return (
    <ProductThumb
      imageUrl={failed ? null : imageUrl}
      onError={() => setFailed(true)}
    />
  );
}

const cellSx = { py: 0.5, px: 0.75, verticalAlign: "top" } as const;
const numericCellSx = {
  ...cellSx,
  whiteSpace: "nowrap",
  textAlign: "right",
} as const;

function ItemRow({
  item,
  amount,
}: {
  item: QuotePreviewItem;
  amount: (value: number | null) => string;
}) {
  const { t } = useTranslate("Assistant");
  const { quantityUnitLabels } = useQuantityUnits();
  const [details, setDetails] = React.useState(false);
  const { articleNumber, supplierId } = item;
  const identity =
    articleNumber && supplierId ? { articleNumber, supplierId } : null;
  const muted = item.alternative || item.optional;
  const unit =
    item.quantityUnit === null
      ? null
      : (Object.entries(quantityUnitLabels).find(
          ([code]) => code === item.quantityUnit
        )?.[1] ?? item.quantityUnit);
  const quantity =
    item.quantity === null
      ? "–"
      : [item.quantity, unit].filter(v => v !== null && v !== "").join(" ");
  return (
    <Box
      component="tr"
      sx={{
        color: muted ? "text.secondary" : "text.primary",
        fontStyle: item.alternative ? "italic" : "normal",
      }}
    >
      <Box component="td" sx={{ ...cellSx, whiteSpace: "nowrap" }}>
        <Typography variant="caption" color="inherit">
          {item.number}
        </Typography>
      </Box>
      <Box component="td" sx={{ ...cellSx, width: "100%" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          {item.imageUrl &&
            (identity ? (
              <ProductItemThumb item={item} {...identity} />
            ) : (
              <PlainItemThumb imageUrl={item.imageUrl} />
            ))}
          <Box sx={{ minWidth: 0 }}>
            {identity ? (
              <>
                <Box
                  component="button"
                  type="button"
                  aria-label={t("Details for {title}", {
                    title: item.title,
                    ns: "Assistant",
                  })}
                  onClick={() => setDetails(true)}
                  sx={{
                    all: "unset",
                    cursor: "pointer",
                    typography: "body2",
                    "&:hover": { textDecoration: "underline" },
                    "&:focus-visible": { outline: "2px solid" },
                  }}
                >
                  {item.title}
                </Box>
                {details && (
                  <ProductDetails
                    product={identity}
                    onClose={() => setDetails(false)}
                  />
                )}
              </>
            ) : (
              <Typography variant="body2" color="inherit">
                {item.title}
              </Typography>
            )}
            {item.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
              >
                {item.description}
              </Typography>
            )}
            {item.note && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
              >
                {noteText(t, item.note)}
              </Typography>
            )}
            {muted && (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ mt: 0.25, alignItems: "center" }}
              >
                {item.optional && (
                  <Chip size="small" variant="outlined" label={t("Optional")} />
                )}
                {item.alternative && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("Alternative")}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {t("Not in the sum")}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
      <Box component="td" sx={numericCellSx}>
        <Typography variant="body2" color="inherit">
          {quantity}
        </Typography>
      </Box>
      <Box component="td" sx={numericCellSx}>
        <Typography variant="body2" color="inherit">
          {amount(item.unitPrice)}
        </Typography>
      </Box>
      <Box component="td" sx={numericCellSx}>
        <Typography
          variant="body2"
          color="inherit"
          sx={{ textDecoration: item.optional ? "line-through" : "none" }}
        >
          {amount(item.total)}
        </Typography>
      </Box>
    </Box>
  );
}

function SectionBlock({
  section,
  amount,
}: {
  section: QuotePreviewSection;
  amount: (value: number | null) => string;
}) {
  const { t } = useTranslate(["Assistant", "Quote"]);
  const [expanded, setExpanded] = React.useState(false);
  const limit = expanded
    ? section.items.length
    : QUOTE_PREVIEW_CARD_ITEMS_PER_SECTION;
  const shown = section.items.slice(0, limit);
  const hidden = section.items.length - shown.length;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "baseline",
          borderBottom: 1,
          borderColor: "divider",
          pb: 0.25,
        }}
      >
        <Typography variant="subtitle2">{section.number}</Typography>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {section.title ?? t("Items", { ns: "Assistant" })}
        </Typography>
        <Typography variant="subtitle2">{amount(section.subtotal)}</Typography>
      </Stack>
      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: 1, borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr" sx={{ color: "text.secondary" }}>
              <Box component="th" sx={cellSx} />
              <Box component="th" sx={cellSx} />
              <Box component="th" sx={{ ...numericCellSx, fontWeight: 400 }}>
                <Typography variant="caption" color="inherit">
                  {t("Quantity", { ns: "Quote" })}
                </Typography>
              </Box>
              <Box component="th" sx={{ ...numericCellSx, fontWeight: 400 }}>
                <Typography variant="caption" color="inherit">
                  {t("Unit Price", { ns: "Quote" })}
                </Typography>
              </Box>
              <Box component="th" sx={{ ...numericCellSx, fontWeight: 400 }}>
                <Typography variant="caption" color="inherit">
                  {t("Total", { ns: "Quote" })}
                </Typography>
              </Box>
            </Box>
          </Box>
          <tbody>
            {shown.map(item => (
              <ItemRow key={item.number} item={item} amount={amount} />
            ))}
          </tbody>
        </Box>
      </Box>
      {hidden > 0 && (
        <Button
          size="small"
          color="inherit"
          onClick={() => setExpanded(true)}
          sx={{ color: "text.secondary", textTransform: "none" }}
        >
          {t("… {count, plural, one {# more item} other {# more items}}", {
            count: hidden,
            ns: "Assistant",
          })}
        </Button>
      )}
    </Box>
  );
}

function TotalLine({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  const weight = bold ? 700 : 400;
  return (
    <Stack
      direction="row"
      spacing={3}
      sx={{ justifyContent: "space-between", minWidth: 220 }}
    >
      <Typography variant="body2" sx={{ fontWeight: weight }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: weight }}>
        {value}
      </Typography>
    </Stack>
  );
}

function answerText(value: string | number | boolean | null): string {
  return value === null ? "–" : String(value);
}

function Disclaimer({
  view,
  vatPercent,
}: {
  view: QuotePreviewViewSpec;
  vatPercent: string;
}) {
  const { t } = useTranslate("Assistant");
  const sentences = [
    t(
      "Preview: final prices and layout may differ in the quote (without letterhead, template calculations and discounts)."
    ),
  ];
  if (view.missingPrices > 0) {
    sentences.push(
      t(
        "{count, plural, one {# item without a price is not included in the sums.} other {# items without a price are not included in the sums.}}",
        { count: view.missingPrices, ns: "Assistant" }
      )
    );
  }
  if (view.vatAssumed) {
    sentences.push(
      t("VAT assumed at {rate} %.", { rate: vatPercent, ns: "Assistant" })
    );
  }
  if (view.notEvaluated) {
    sentences.push(
      view.answers.length > 0
        ? t(
            "Formulas, questions and decisions of the template are not evaluated. Assumed answers: {answers}.",
            {
              answers: view.answers
                .map(answer => `${answer.key}: ${answerText(answer.value)}`)
                .join(", "),
              ns: "Assistant",
            }
          )
        : t(
            "Formulas, questions and decisions of the template are not evaluated."
          )
    );
  }
  if (view.contentFromAnswers) {
    sentences.push(
      t("The content follows from the answers in the questionnaire.")
    );
  }
  return (
    <Alert
      severity="info"
      icon={<InfoIcon fontSize="small" />}
      sx={{ mt: 2, py: 0 }}
    >
      {sentences.join(" ")}
    </Alert>
  );
}

export function QuotePreviewView({ view }: { view: QuotePreviewViewSpec }) {
  const { t } = useTranslate(["Assistant", "Quote"]);
  const { getFormattedPrice, getFormattedFloat } = useFormatting();
  const amount = (value: number | null) =>
    formatQuoteAmount(value, getFormattedPrice);
  const vatPercent = getFormattedFloat(view.vatRate * 100);
  return (
    <Box
      sx={{
        bgcolor: "common.white",
        color: "text.primary",
        width: 1,
        p: 1,
        overflowX: "auto",
      }}
    >
      <Typography variant="subtitle1" component="h3">
        {view.title}
      </Typography>
      {(view.customer || view.project) && (
        <Typography variant="body2" color="text.secondary">
          {[view.customer, view.project].filter(Boolean).join(" · ")}
        </Typography>
      )}
      {view.sections.map(section => (
        <SectionBlock key={section.number} section={section} amount={amount} />
      ))}
      <Stack
        spacing={0.25}
        sx={{
          alignItems: "flex-end",
          mt: 2,
          pt: 1,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <TotalLine
          label={t("Net price", { ns: "Quote" })}
          value={amount(view.totals.net)}
        />
        <TotalLine
          label={`${t("VAT", { ns: "Quote" })} (${vatPercent}%)`}
          value={amount(view.totals.vat)}
        />
        <TotalLine
          label={t("Gross", { ns: "Assistant" })}
          value={amount(view.totals.gross)}
          bold
        />
      </Stack>
      <Disclaimer view={view} vatPercent={vatPercent} />
    </Box>
  );
}
